import { useState, useEffect, useRef } from 'react';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import type { Map } from '@birdi/js-sdk/src/public/maps';

/**
 * Custom hook to fetch map covers asynchronously
 * @param maps - Array of maps or a function that returns maps
 * @returns Object with mapCovers state and a function to merge covers into maps
 */
export function useMapCovers(
  maps: Map[] | (() => Map[] | undefined),
): {
  mapCovers: Record<string, string>;
  getMapsWithCovers: (mapsToMerge: Map[]) => Map[];
} {
  const [mapCovers, setMapCovers] = useState<Record<string, string>>({});
  const requestedCoversRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const mapsArray = typeof maps === 'function' ? maps() : maps;
    if (!mapsArray || mapsArray.length === 0) return;

    // Filter out undefined/null maps
    const validMaps = mapsArray.filter((map): map is Map => !!map);
    if (validMaps.length === 0) return;

    const mapIds = validMaps
      .map((map) => map.id)
      .filter((id: string) => {
        // Only fetch covers for maps we haven't requested yet
        return !requestedCoversRef.current.has(id);
      });

    if (mapIds.length === 0) return;

    // Mark these as requested
    mapIds.forEach((id) => requestedCoversRef.current.add(id));

    // Fetch covers one by one in parallel - each updates state as it completes
    // This allows browser caching to work properly with GET requests
    mapIds.forEach((mapId) => {
      mapsAPI
        .getMapCover(stroot('gmpcvr'), mapId)
        .then((res) => {
          if (res.body?.mapCover) {
            // Update state immediately as each cover completes
            setMapCovers((prev) => ({
              ...prev,
              [mapId]: res.body!.mapCover!,
            }));
          }
        })
        .catch((error) => {
          // Silently fail - covers are not critical, don't block other requests
          console.error(`Failed to fetch cover for map ${mapId}:`, error);
        });
    });
  }, [maps]);

  const getMapsWithCovers = (mapsToMerge: Map[]): Map[] => {
    return mapsToMerge.map((map) => ({
      ...map,
      mapCover: mapCovers[map.id] || map.mapCover || null,
    }));
  };

  return { mapCovers, getMapsWithCovers };
}

